import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { 
  UsersIcon, 
  DocumentTextIcon, 
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface DashboardStats {
  totalUsers: number;
  totalListings: number;
  totalMessages: number;
  activeListings: number;
  pendingApprovals: number;
  revenueThisMonth: number;
  newUsersThisWeek: number;
  listingsThisMonth: number;
}

const AdminDashboard: React.FC = () => {
  const { currentUser, userProfile } = useAuthContext();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalListings: 0,
    totalMessages: 0,
    activeListings: 0,
    pendingApprovals: 0,
    revenueThisMonth: 0,
    newUsersThisWeek: 0,
    listingsThisMonth: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading dashboard data
    const loadDashboardData = async () => {
      setLoading(true);
      // In a real app, this would fetch data from Firebase
      setTimeout(() => {
        setStats({
          totalUsers: 1247,
          totalListings: 892,
          totalMessages: 2156,
          activeListings: 743,
          pendingApprovals: 23,
          revenueThisMonth: 15420,
          newUsersThisWeek: 89,
          listingsThisMonth: 156
        });
        setLoading(false);
      }, 1000);
    };

    loadDashboardData();
  }, []);

  // Check if user is admin
  if (!currentUser || userProfile?.profile?.userType !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, change }: {
    title: string;
    value: string | number;
    icon: React.ComponentType<any>;
    color: string;
    change?: string;
  }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <p className="text-sm text-green-600 mt-1">{change}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );

  const QuickActionCard = ({ title, description, icon: Icon, href, color }: {
    title: string;
    description: string;
    icon: React.ComponentType<any>;
    href: string;
    color: string;
  }) => (
    <Link to={href} className="block">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center space-x-4">
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <>
      <Helmet>
        <title>Admin Dashboard | BizBuyMax.com</title>
        <meta name="description" content="Admin dashboard for managing the business marketplace" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Welcome back, {userProfile?.profile?.firstName || 'Admin'}</p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  Last updated: {new Date().toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                  title="Total Users"
                  value={stats.totalUsers.toLocaleString()}
                  icon={UsersIcon}
                  color="bg-blue-500"
                  change="+12% this month"
                />
                <StatCard
                  title="Active Listings"
                  value={stats.activeListings.toLocaleString()}
                  icon={DocumentTextIcon}
                  color="bg-green-500"
                  change="+8% this month"
                />
                <StatCard
                  title="Total Messages"
                  value={stats.totalMessages.toLocaleString()}
                  icon={ChatBubbleLeftRightIcon}
                  color="bg-purple-500"
                  change="+15% this month"
                />
                <StatCard
                  title="Revenue (This Month)"
                  value={`$${stats.revenueThisMonth.toLocaleString()}`}
                  icon={CurrencyDollarIcon}
                  color="bg-yellow-500"
                  change="+23% vs last month"
                />
              </div>

              {/* Quick Actions */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <QuickActionCard
                    title="Manage Users"
                    description="View and manage user accounts"
                    icon={UsersIcon}
                    href="/admin/users"
                    color="bg-blue-500"
                  />
                  <QuickActionCard
                    title="Review Listings"
                    description="Approve or reject pending listings"
                    icon={DocumentTextIcon}
                    href="/admin/listings"
                    color="bg-green-500"
                  />
                  <QuickActionCard
                    title="View Messages"
                    description="Monitor user communications"
                    icon={ChatBubbleLeftRightIcon}
                    href="/admin/messages"
                    color="bg-purple-500"
                  />
                </div>
              </div>

              {/* Recent Activity & Alerts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recent Activity */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">New user registration: John Smith</p>
                        <p className="text-xs text-gray-500">2 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">New listing submitted: Coffee Shop in Toronto</p>
                        <p className="text-xs text-gray-500">15 minutes ago</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">Message sent between buyer and seller</p>
                        <p className="text-xs text-gray-500">1 hour ago</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">Payment received: Premium package</p>
                        <p className="text-xs text-gray-500">2 hours ago</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Alerts & Notifications */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Alerts & Notifications</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-900">Pending Approvals</p>
                        <p className="text-xs text-red-700">{stats.pendingApprovals} listings need review</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-900">System Maintenance</p>
                        <p className="text-xs text-yellow-700">Scheduled maintenance in 2 hours</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-green-900">System Status</p>
                        <p className="text-xs text-green-700">All systems operational</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Growth</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">New Users</span>
                      <span className="text-sm font-semibold text-gray-900">{stats.newUsersThisWeek}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">New Listings</span>
                      <span className="text-sm font-semibold text-gray-900">{stats.listingsThisMonth}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Conversion Rate</span>
                      <span className="text-sm font-semibold text-gray-900">3.2%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Categories</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Restaurants</span>
                      <span className="text-sm font-semibold text-gray-900">156</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Retail</span>
                      <span className="text-sm font-semibold text-gray-900">134</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Services</span>
                      <span className="text-sm font-semibold text-gray-900">98</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Page Load Time</span>
                      <span className="text-sm font-semibold text-gray-900">1.2s</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Uptime</span>
                      <span className="text-sm font-semibold text-gray-900">99.9%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Active Sessions</span>
                      <span className="text-sm font-semibold text-gray-900">1,247</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard; 